import React, {
  forwardRef,
  RefObject,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { AflAdrLosseKopersService } from 'services/AflAdrLosseKopersService'
import { VestigingEnum } from 'shared/models/Enum'
import { IKlant } from 'shared/models/IKlant'
import { IKlantAfleverafspraak } from 'shared/models/IKlantAfleverafspraak'
import { ILocatieGlnModel } from 'shared/models/ILocatieGlnModel'
import {
  convertToDate,
  convertToDateTime,
} from 'shared/service/helpers/DateUtilities'
import AddressSearch from '../../../shared/components/AddressSearch'
import PeriodSelect from '../../../shared/components/PeriodSelect'
import { IAfleverafspraakLocatieKoppeling } from '../../../shared/models/IAfleverafspraakLocatieKoppeling'
import {
  IModalObjectReference,
  IModalReference,
} from '../../../shared/models/References'

interface IProps {
  customer: IKlant
  service: AflAdrLosseKopersService
  deliveryAgreement: IKlantAfleverafspraak
  onSave?: () => void
  onHide?: () => void
  onLoading?: (isLoading: boolean) => void
  warehouse: VestigingEnum
}

function LinkLocation(
  props: IProps,
  ref: RefObject<IModalObjectReference<IAfleverafspraakLocatieKoppeling>>
) {
  const periodRef = useRef<IModalReference>()
  const addressRef = useRef<IModalReference>()

  const [existingLink, setExistingLink] =
    useState<IAfleverafspraakLocatieKoppeling>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [selectedAddress, setSelectedAddress] = useState<ILocatieGlnModel>()
  const [saveSuccessfull, setSaveSuccessfull] = useState(false)

  useImperativeHandle(ref, () => ({
    show: (link: IAfleverafspraakLocatieKoppeling) => {
      show(link)
    },
    hide: () => {
      hide()
    },
  }))

  const hide = () => {
    periodRef.current.hide()
    addressRef.current.hide()
    setSaveSuccessfull(false)
    if (!!props.onHide) {
      props.onHide()
    }
  }

  const show = (link: IAfleverafspraakLocatieKoppeling) => {
    clearState(link)
    if (!!link) {
      setSaveSuccessfull(false)
      periodRef.current.show()
    } else {
      addressRef.current.show()
    }
  }

  const clearState = (link: IAfleverafspraakLocatieKoppeling) => {
    setErrorMessage(null)
    if (!!link) {
      setExistingLink(link)
      setStartDate(convertToDateTime(link.ingangsDatumtijd))
      setEndDate(convertToDateTime(link.eindDatumtijd))
    } else {
      setExistingLink(null)
      setStartDate(new Date())
      setEndDate(new Date())
    }
  }

  const addressClick = (address: ILocatieGlnModel) => {
    setSelectedAddress(address)
    if (!!address) {
      setSaveSuccessfull(false)
      periodRef.current.show()
    }
  }

  const onPeriodSelected = (newStartDate: Date, newEndDate: Date) => {
    setStartDate(newStartDate)
    setEndDate(newEndDate)
    const newLink = getLink(newStartDate, newEndDate)
    if (!!props.onLoading) {
      props.onLoading(true)
    }
    props.service
      .saveLocationLink(newLink, props.customer)
      .then(result => {
        if (result.success) {
          setErrorMessage(null)
          setSaveSuccessfull(true)
          if (!!props.onSave) {
            props.onSave()
          }
        } else {
          setSaveSuccessfull(false)
          setErrorMessage(result.errorMessage)
        }
      })
      .finally(() => {
        if (!!props.onLoading) {
          props.onLoading(false)
        }
      })
  }

  const getLink = (
    newStartDate: Date,
    newEndDate: Date
  ): IAfleverafspraakLocatieKoppeling => {
    if (!!existingLink) {
      return {
        afleverafspraakLocatieKoppelingId:
          existingLink.afleverafspraakLocatieKoppelingId,
        eindDatumtijd: newEndDate,
        ingangsDatumtijd:
          convertToDate(existingLink.ingangsDatumtijd) <= convertToDate()
            ? null
            : newStartDate,
        klantAfleverAfspraakId: existingLink.klantAfleverAfspraakId,
        klantAfleverafspraak: existingLink.klantAfleverafspraak,
        shipToLocationGln: existingLink.shipToLocationGln,
        shipToLocationGlnId: existingLink.shipToLocationGlnId,
        routeToKlantId: 0,
        routeToLocationGlnId: 0,
        magCombineren: false,
        dagen: null,
        orderTypes: null,
        koppelingType: null,
      }
    } else {
      return {
        afleverafspraakLocatieKoppelingId: 0,
        eindDatumtijd: newEndDate,
        ingangsDatumtijd: newStartDate,
        klantAfleverAfspraakId: props.deliveryAgreement.klantAfleverafspraakId,
        klantAfleverafspraak: props.deliveryAgreement,
        shipToLocationGln: selectedAddress,
        shipToLocationGlnId: selectedAddress.locatieGlnId,
        routeToKlantId: 0,
        routeToLocationGlnId: 0,
        magCombineren: false,
        dagen: null,
        orderTypes: null,
        koppelingType: null,
      }
    }
  }

  return (
    <React.Fragment>
      <AddressSearch
        ref={addressRef}
        onLinkedAddress={addressClick}
        warehouse={props.warehouse}
      />

      <PeriodSelect
        ref={periodRef}
        initialStartDate={startDate}
        initialEndDate={endDate}
        maxEndDate={props.deliveryAgreement?.eindDatum ? new Date(props.deliveryAgreement.eindDatum) : undefined}
        disabledStartDate={
          !!existingLink &&
          convertToDate(existingLink.ingangsDatumtijd) <= convertToDate()
        }
        onPeriodSelected={onPeriodSelected}
        canClose={saveSuccessfull}
        errorMessage={errorMessage}
      />
    </React.Fragment>
  )
}
export default forwardRef(LinkLocation)
